import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import './MoreMenuComponent.css';

export interface MoreMenuItem {
    id: string;
    label: string;
    disabled?: boolean;
    hint?: string | null;   // One line under the label, e.g. why the item is disabled
    onSelect: () => void;
}

interface MoreMenuProps {
    label: string;          // The button's accessible name
    items: MoreMenuItem[];
}

/**
 * A "…" button with a dropdown of actions, following the menu-button pattern: Enter/Space/↓ open,
 * ↑/↓/Home/End move, Enter selects, Esc closes and returns focus to the button, a click outside closes.
 */
const MoreMenuComponent = ({ label, items }: MoreMenuProps) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const focusOnOpen = useRef(false);

    const menuItems = () => [...rootRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []];
    const focusItem = (i: number) => {
        const els = menuItems();
        if (els.length) els[(i + els.length) % els.length].focus();
    };

    useEffect(() => {
        if (!open) return;
        if (focusOnOpen.current) {
            focusOnOpen.current = false;
            focusItem(0);
        }
        const onPointerDown = (e: PointerEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [open]);

    const openMenu = (viaKeyboard: boolean) => {
        focusOnOpen.current = viaKeyboard;
        setOpen(true);
    };

    const close = () => {
        setOpen(false);
        buttonRef.current?.focus();
    };

    const onButtonKeyDown = (e: KeyboardEvent) => {
        // Enter and Space open through the button's click, but keyboard users expect the first item focused
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openMenu(true);
        }
    };

    const onMenuKeyDown = (e: KeyboardEvent) => {
        const i = menuItems().indexOf(document.activeElement as HTMLElement);
        const moves: Record<string, number> = { ArrowDown: i + 1, ArrowUp: i - 1, Home: 0, End: menuItems().length - 1 };
        if (e.key in moves) {
            e.preventDefault();
            focusItem(moves[e.key]);
        } else if (e.key === 'Tab') {
            setOpen(false);
        }
    };

    return (
        <div ref={rootRef} className="moreMenu"
             onKeyDown={(e) => { if (e.key === 'Escape' && open) { e.preventDefault(); close(); } }}>
            <button type="button" ref={buttonRef} className={`moreMenu-button${open ? ' is-open' : ''}`}
                    aria-label={label} title={label} aria-haspopup="menu" aria-expanded={open}
                    onClick={() => open ? setOpen(false) : openMenu(false)} onKeyDown={onButtonKeyDown}>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                    <path d="M3.5 8h.01M8 8h.01M12.5 8h.01" />
                </svg>
            </button>
            {open &&
                <div className="moreMenu-menu" role="menu" aria-label={label} onKeyDown={onMenuKeyDown}>
                    {items.map(item => {
                        const hintId = item.hint ? `moreMenu-hint-${item.id}` : undefined;
                        return (
                            <button key={item.id} type="button" role="menuitem" tabIndex={-1} className="moreMenu-item"
                                    aria-disabled={item.disabled ? 'true' : undefined} aria-describedby={hintId}
                                    onClick={() => {
                                        if (item.disabled) return;
                                        close();
                                        item.onSelect();
                                    }}>
                                <span className="moreMenu-label">{item.label}</span>
                                {item.hint && <span id={hintId} className="moreMenu-hint">{item.hint}</span>}
                            </button>
                        );
                    })}
                </div>}
        </div>
    );
};

export default MoreMenuComponent;
